import {
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExButton,
    ExIcon,
    ExLoader,
    IconSize,
    LoaderSize,
    LoaderVariant,
} from '@boomi/exosphere';
import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import type { Action, Trigger } from '../types/types';
import { base64SvgToSrc } from '../utils/utils';

import './selected-connector.css';

const SelectedConnector = ({
    itemData,
    onReset,
    onDelete,
    isConnectorAuthenticated,
    isLoadingAuthenticationStatus,
}: {
    itemData: Trigger | Action;
    onReset?: (() => void) | undefined;
    onDelete?: (() => void) | undefined;
    isConnectorAuthenticated: boolean;
    isLoadingAuthenticationStatus: boolean;
}) => {
    const { language, connectors } = useApp();
    const connector = connectors[itemData.providerId];

    return (
        <div className="connector-wrapper" data-testid="connector">
            {/* connector icon */}
            {connector ? (
                <div className="icon-circle">
                    <img className="icon" src={base64SvgToSrc(connector.icon)} alt="" />
                </div>
            ) : (
                <ExIcon icon="In progress" size={IconSize.M} hide-browser-tooltip />
            )}
            {/* connector name */}
            <h3 className="title lg">{connector.displayName}</h3>
            {/* connector auth. status */}
            {/* Show auth. status only if a Trigger or Action have been selected because it depends
                on them. */}
            {!!itemData.id &&
                (isLoadingAuthenticationStatus ? (
                    <div
                        key="loading-badge"
                        className="loading-badge"
                        data-testid={`${connector.id}_authStatusLoading`}
                    >
                        <ExLoader
                            size={LoaderSize.MEDIUM}
                            variant={LoaderVariant.COMPONENT_LEVEL_MEDIUM}
                        />
                        {translations[language].COMMON_loading}
                    </div>
                ) : (
                    <div
                        className={`status-badge ${isConnectorAuthenticated ? 'authenticated' : 'not-authenticated'}`}
                        title={
                            isConnectorAuthenticated
                                ? translations[language].TASK_authenticated
                                : translations[language].TASK_not_authenticated
                        }
                    >
                        <ExIcon
                            className="badge-icon"
                            icon={isConnectorAuthenticated ? 'Authenticated' : 'Error'}
                            hide-browser-tooltip
                            size={IconSize.S}
                        />
                        <span className="badge-text">
                            {isConnectorAuthenticated
                                ? translations[language].TASK_authenticated
                                : translations[language].TASK_not_authenticated}
                        </span>
                    </div>
                ))}
            {/* connector actions */}
            {/* Event stopPropagation does not work properly on ExIconButton,
                so as a workaround using ExButton with an ExIcon inside. */}
            {onReset && (
                <ExButton
                    data-testid="connectorResetAction"
                    className="exo-button reset-button"
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.DEFAULT}
                    type={ButtonType.SECONDARY}
                    onClick={onReset}
                >
                    {translations[language].COMMON_change}
                </ExButton>
            )}
            {onDelete && (
                <ExButton
                    data-testid="connectorDeleteAction"
                    className="exo-button-action"
                    flavor={ButtonFlavor.RISKY}
                    size={ButtonSize.LARGE}
                    type={ButtonType.TERTIARY}
                    onClick={(e: Event) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                >
                    <ExIcon icon="trash" hide-browser-tooltip />
                </ExButton>
            )}
        </div>
    );
};

export default SelectedConnector;
